import { Link } from "react-router-dom";

/* Five distinct directions for the homepage aesthetic.
   Each card is a self-contained mini rendering (not a link — this is a
   comparison gallery). Pick a number and tell me which to apply. */

function Frame({ n, name, reference, children }) {
  return (
    <div className="relative">
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <div className="text-xs font-mono text-slate-500">Option {String(n).padStart(2, "0")}</div>
          <div className="text-xl font-semibold text-slate-900 mt-0.5">{name}</div>
          <div className="text-xs text-slate-500 mt-0.5">Feels like: {reference}</div>
        </div>
      </div>
      <div className="rounded-lg border border-slate-300 shadow-sm overflow-hidden bg-white">
        <div className="flex items-center gap-1.5 px-3 py-2 border-b border-slate-200 bg-slate-100">
          <span className="h-2.5 w-2.5 rounded-full bg-red-400" />
          <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
          <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
          <span className="ml-3 text-[10px] font-mono text-slate-500">whitmorecapital.com</span>
        </div>
        <div className="h-[420px] overflow-hidden">{children}</div>
      </div>
    </div>
  );
}

/* ---------- 01: Traditional Old-Money (JP Morgan Private Bank) ---------- */
function Template1() {
  return (
    <div className="h-full flex flex-col" style={{ background: "#F5EFE4", fontFamily: "Georgia, 'Times New Roman', serif" }}>
      <div className="flex items-center justify-between px-8 py-4 border-b" style={{ borderColor: "#2E1F0E", background: "#0F1419" }}>
        <div className="text-[#F5EFE4] tracking-[0.3em] text-xs uppercase" style={{ fontFamily: "Georgia, serif" }}>WHITMORE ∙ EST. MCMXI</div>
        <div className="flex gap-6 text-[10px] tracking-[0.25em] uppercase text-[#C9A96E]">
          <span>The Firm</span><span>Capabilities</span><span>Insights</span><span>Contact</span>
        </div>
      </div>
      <div className="flex-1 flex">
        <div className="flex-1 px-10 py-8" style={{ background: "url('data:image/svg+xml,%3Csvg xmlns=\"http://www.w3.org/2000/svg\" width=\"40\" height=\"40\"%3E%3Cpath d=\"M0 0h40v1H0zM0 0v40h1V0z\" fill=\"%232E1F0E\" opacity=\"0.03\"/%3E%3C/svg%3E')" }}>
          <div className="text-[9px] tracking-[0.3em] uppercase text-[#8B6D3F] mb-6">— A Private Merchant Bank —</div>
          <div className="text-[52px] leading-[0.95] text-[#1A1408]" style={{ fontFamily: "'Playfair Display', Georgia, serif", fontStyle: "italic", fontWeight: 400 }}>
            The stewardship
            <br />of enduring
            <br /><span className="text-[#8B6D3F]">enterprises.</span>
          </div>
          <div className="mt-6 text-[13px] leading-relaxed text-[#3D2E1A] max-w-md" style={{ fontFamily: "Georgia, serif" }}>
            For four generations, family enterprises and closely-held businesses have entrusted their financing to our discretion.
          </div>
          <div className="mt-8 flex gap-6 text-[10px] tracking-[0.3em] uppercase">
            <span className="text-[#1A1408] border-b-2 border-[#C9A96E] pb-1">Apply Confidentially</span>
            <span className="text-[#8B6D3F]">Client Access</span>
          </div>
        </div>
        <div className="w-40 border-l border-[#8B6D3F]/30 p-6 bg-[#0F1419] text-[#C9A96E]">
          <div className="text-[9px] tracking-[0.25em] uppercase opacity-70 mb-3">Offices</div>
          <div className="space-y-2 text-[11px]" style={{ fontFamily: "Georgia, serif" }}>
            <div>New York</div><div>London</div><div>Geneva</div><div>Hong Kong</div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- 02: Institutional Modernist (current — Venture Nationals/Blue Owl) ---------- */
function Template2() {
  return (
    <div className="h-full flex flex-col" style={{ background: "#FFFFFF", fontFamily: "'DM Sans', sans-serif" }}>
      <div className="flex items-center justify-between px-8 py-4 border-b border-[#000000]/15">
        <div className="text-[#000000]"><span style={{ fontFamily: "'Instrument Serif', serif" }} className="text-lg">Venture Nationals</span> <span className="text-[10px] tracking-[0.2em] uppercase opacity-60 ml-1">Capital</span></div>
        <div className="flex gap-6 text-[10px] tracking-[0.16em] uppercase text-[#000000]/70">
          <span>Firm</span><span>Capabilities</span><span>Approach</span><span>Sign In</span>
          <span className="text-[#000000] border-b border-[#000000]">Apply Now →</span>
        </div>
      </div>
      <div className="flex-1 grid grid-cols-3 gap-6 px-10 py-8">
        <div className="col-span-2">
          <div className="text-[10px] tracking-[0.16em] uppercase text-[#000000]/60">Est. 2011 · Private Credit</div>
          <div className="mt-4 text-[68px] leading-[0.9] text-[#000000]" style={{ fontFamily: "'Fraunces', serif", fontWeight: 300 }}>
            Capital,<br /><span style={{ fontStyle: "italic" }}>considered.</span>
          </div>
          <div className="mt-6 text-[13px] leading-relaxed text-[#000000]/75 max-w-sm">
            Financing closely-held businesses, sponsor-backed platforms, and family offices from $500K to $250M.
          </div>
          <div className="mt-6 flex items-center gap-6">
            <div className="flex items-center gap-3 text-[10px] tracking-[0.16em] uppercase text-[#000000]">
              <span className="h-px w-10 bg-[#000000]" /> Apply Now
            </div>
            <span className="text-[10px] tracking-[0.16em] uppercase text-[#000000]/60">Sign In</span>
          </div>
        </div>
        <div className="bg-slate-700 rounded-sm" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1554469384-e58fac16e23a?auto=format&fit=crop&w=400&q=80')", backgroundSize: "cover", filter: "grayscale(30%) sepia(10%)" }} />
      </div>
    </div>
  );
}

/* ---------- 03: Corporate Sharp (Goldman/Blackstone) ---------- */
function Template3() {
  return (
    <div className="h-full flex flex-col" style={{ background: "#FFFFFF", fontFamily: "'Inter', 'Helvetica Neue', sans-serif" }}>
      <div className="flex items-center justify-between px-8 py-4 border-b border-slate-200">
        <div className="flex items-center gap-2">
          <div className="h-6 w-6" style={{ background: "#001E5A" }} />
          <span className="text-[15px] font-bold tracking-tight text-[#001E5A]">WHITMORE CAPITAL</span>
        </div>
        <div className="flex gap-5 text-[12px] text-slate-700 font-medium">
          <span>Firm</span><span>Capabilities</span><span>Insights</span><span>Careers</span>
          <span className="px-3 py-1 bg-[#001E5A] text-white">Client Login</span>
        </div>
      </div>
      <div className="flex-1 relative overflow-hidden">
        <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, #001E5A 0%, #003182 100%)" }} />
        <div className="absolute right-0 top-0 h-full w-[45%] opacity-40" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1518005020951-eccb494ad742?auto=format&fit=crop&w=800&q=80')", backgroundSize: "cover", mixBlendMode: "overlay" }} />
        <div className="relative h-full flex flex-col justify-center px-10">
          <div className="text-[11px] tracking-[0.2em] uppercase text-[#FF7A00] font-bold mb-3">Private Credit Solutions</div>
          <div className="text-[52px] leading-[1] text-white font-bold tracking-tight max-w-2xl">
            Purpose-built capital for<br />ambitious enterprises.
          </div>
          <div className="mt-5 text-[14px] text-white/80 max-w-lg leading-relaxed">
            From working-capital facilities to broadly syndicated debt — a single relationship across sixteen mandates.
          </div>
          <div className="mt-8 flex gap-3">
            <button className="px-6 py-3 bg-[#FF7A00] text-white text-[12px] font-bold tracking-wide">APPLY NOW →</button>
            <button className="px-6 py-3 border border-white/40 text-white text-[12px] font-bold tracking-wide">CLIENT LOGIN</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- 04: Warm Boutique Family Office (One Rock / Waverly Advisors) ---------- */
function Template4() {
  return (
    <div className="h-full flex flex-col" style={{ background: "#EAE3D2", fontFamily: "'Cormorant Garamond', 'DM Sans', serif" }}>
      <div className="flex items-center justify-between px-10 py-5">
        <div style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }} className="text-[22px] text-[#3D2E1A]">Venture Nationals</div>
        <div className="flex gap-8 text-[11px] tracking-wider text-[#5C4A2E]" style={{ fontFamily: "'DM Sans', sans-serif" }}>
          <span>Our Family</span><span>Philosophy</span><span>Services</span><span>Contact</span>
        </div>
      </div>
      <div className="flex-1 grid grid-cols-5 gap-8 px-10 py-6">
        <div className="col-span-3 flex flex-col justify-center">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#8B6D3F] mb-3">A Family Office · Since 2011</div>
          <div className="text-[64px] leading-[0.95] text-[#2C1F0A]" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 400 }}>
            Capital,<br />
            <span style={{ fontStyle: "italic", color: "#8B6D3F" }}>with intention.</span>
          </div>
          <div className="mt-5 text-[14px] leading-[1.6] text-[#4A3820] max-w-md" style={{ fontFamily: "'DM Sans', sans-serif" }}>
            We finance the families and founders who build things that last. Small enough to know your name. Large enough to move markets.
          </div>
          <div className="mt-6 flex items-center gap-6">
            <button className="px-5 py-2.5 bg-[#8B6D3F] text-[#F5EFE4] text-[11px] tracking-[0.15em] uppercase" style={{ fontFamily: "'DM Sans', sans-serif" }}>Start a Conversation</button>
            <span className="text-[11px] tracking-[0.15em] uppercase text-[#5C4A2E]" style={{ fontFamily: "'DM Sans', sans-serif" }}>Sign In →</span>
          </div>
        </div>
        <div className="col-span-2 grid grid-cols-2 grid-rows-2 gap-2">
          <div style={{ backgroundImage: "url('https://images.unsplash.com/photo-1531384441138-2736e62e0919?auto=format&fit=crop&w=300&q=80')", backgroundSize: "cover", filter: "sepia(20%) contrast(0.95)" }} className="rounded-sm" />
          <div className="bg-[#8B6D3F] rounded-sm" />
          <div className="bg-[#3D2E1A] rounded-sm" />
          <div style={{ backgroundImage: "url('https://images.unsplash.com/photo-1554469384-e58fac16e23a?auto=format&fit=crop&w=300&q=80')", backgroundSize: "cover", filter: "sepia(20%) contrast(0.95)" }} className="rounded-sm" />
        </div>
      </div>
    </div>
  );
}

/* ---------- 05: Swiss Minimalist (Bridgewater / Blackrock research reports) ---------- */
function Template5() {
  return (
    <div className="h-full flex flex-col" style={{ background: "#FFFFFF", fontFamily: "'Neue Haas Grotesk', 'Helvetica', sans-serif" }}>
      <div className="grid grid-cols-12 items-center px-8 py-5 border-b-2 border-black">
        <div className="col-span-3 text-[13px] font-bold tracking-tight">WHITMORE / CAPITAL</div>
        <div className="col-span-9 grid grid-cols-6 gap-4 text-[11px] font-medium uppercase tracking-wider">
          <span>01 · Firm</span><span>02 · Loan Programs</span><span>03 · Insights</span><span>04 · Contact</span>
          <span className="col-start-6 text-right underline">Apply →</span>
        </div>
      </div>
      <div className="flex-1 grid grid-cols-12 gap-4 px-8 py-8">
        <div className="col-span-1 text-[10px] font-mono text-slate-400 pt-1">
          NYC<br />2026<br />№001
        </div>
        <div className="col-span-8">
          <div className="text-[11px] uppercase tracking-[0.2em] font-medium mb-6">— Private Credit &amp; Structured Capital</div>
          <div className="text-[84px] leading-[0.85] tracking-[-0.04em] font-medium">
            Capital<br />underwritten<br />by conviction.
          </div>
          <div className="mt-8 flex items-baseline gap-8">
            <button className="text-[11px] uppercase tracking-[0.2em] font-bold underline">Apply Now</button>
            <button className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Sign In</button>
          </div>
        </div>
        <div className="col-span-3 border-l border-black/20 pl-4">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold mb-3">Assets</div>
          <div className="text-[36px] leading-none font-medium">$4.2B</div>
          <div className="text-[10px] text-slate-500 mt-1">Under management</div>
          <div className="mt-6 text-[10px] uppercase tracking-[0.2em] font-bold mb-3">Files</div>
          <div className="text-[36px] leading-none font-medium">312</div>
          <div className="text-[10px] text-slate-500 mt-1">Funded</div>
        </div>
      </div>
    </div>
  );
}

/* ---------- 06: Dark Editorial (Ramp/Mercury hybrid, but dark & sober) ---------- */
function Template6() {
  return (
    <div className="h-full flex flex-col" style={{ background: "#0A0A0A", fontFamily: "'Söhne', 'Inter', sans-serif" }}>
      <div className="flex items-center justify-between px-8 py-5 border-b border-white/10">
        <div className="flex items-center gap-2 text-white">
          <div className="h-5 w-5 rounded-sm bg-white" />
          <span className="text-[14px] font-medium tracking-tight">Venture Nationals</span>
        </div>
        <div className="flex gap-6 text-[12px] text-white/70">
          <span>Programs</span><span>Approach</span><span>Insights</span><span>Sign in</span>
          <span className="px-3 py-1 bg-white text-black rounded-sm text-[11px] font-medium">Apply</span>
        </div>
      </div>
      <div className="flex-1 relative overflow-hidden">
        <div className="absolute inset-0 opacity-40" style={{ background: "radial-gradient(circle at 70% 30%, rgba(180,120,50,0.4), transparent 60%)" }} />
        <div className="relative h-full flex flex-col justify-center px-10">
          <div className="text-[11px] tracking-wider text-[#C9A96E] mb-3">PRIVATE CREDIT · EST. 2011</div>
          <div className="text-[64px] leading-[1] tracking-[-0.03em] text-white font-medium max-w-3xl">
            Private capital,<br />
            built for the<br />
            <span className="text-[#C9A96E]">long horizon.</span>
          </div>
          <div className="mt-6 text-[15px] text-white/70 max-w-lg leading-relaxed">
            Finance from $500K to $250M across sixteen mandates. One relationship, one workspace, one source of truth.
          </div>
          <div className="mt-8 flex items-center gap-3">
            <button className="px-5 py-2.5 bg-white text-black text-[13px] font-medium rounded-sm">Apply Now →</button>
            <button className="px-5 py-2.5 text-white/80 text-[13px]">Sign in</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function TemplateGallery() {
  const templates = [
    { n: 1, name: "Old-Money Merchant Bank", ref: "JP Morgan Private Bank · Brown Brothers Harriman", C: Template1 },
    { n: 2, name: "Institutional Modernist (current)", ref: "Blue Owl · Ares Management · Coatue", C: Template2 },
    { n: 3, name: "Corporate Sharp", ref: "Goldman Sachs · Blackstone · Morgan Stanley", C: Template3 },
    { n: 4, name: "Boutique Family Office", ref: "One Rock · Waverly · Brentwood Associates", C: Template4 },
    { n: 5, name: "Swiss Minimalist", ref: "Bridgewater · BlackRock Research · Millennium", C: Template5 },
    { n: 6, name: "Dark Editorial", ref: "Founders Fund · Thrive Capital · Sequoia", C: Template6 },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 px-10 py-6 sticky top-0 z-10">
        <div className="max-w-[1600px] mx-auto flex items-center justify-between">
          <div>
            <div className="text-xs font-mono text-slate-500">/ templates</div>
            <h1 className="mt-1 text-2xl font-semibold text-slate-900">Pick a direction.</h1>
            <p className="mt-1 text-sm text-slate-600 max-w-2xl">
              Six distinct aesthetic directions for Venture Nationals Capital. Each is a fully-designed hero mockup — pick a number and tell me to apply it site-wide. The dashboards and inner pages will inherit the same system.
            </p>
          </div>
          <Link to="/" className="text-sm text-slate-600 hover:text-slate-900">← Back to current site</Link>
        </div>
      </header>

      <div className="max-w-[1600px] mx-auto px-10 py-10 grid grid-cols-1 xl:grid-cols-2 gap-12">
        {templates.map(({ n, name, ref, C }) => (
          <Frame key={n} n={n} name={name} reference={ref}>
            <C />
          </Frame>
        ))}
      </div>

      <div className="max-w-[1600px] mx-auto px-10 pb-16">
        <div className="rounded-lg border border-slate-300 bg-white p-6">
          <div className="text-xs font-mono text-slate-500 mb-2">/ how to choose</div>
          <div className="text-lg font-semibold text-slate-900">Which one to pick?</div>
          <ul className="mt-4 grid grid-cols-2 gap-x-8 gap-y-2 text-sm text-slate-700">
            <li><b>Option 01</b> — if you want to feel like a 100-year-old bank. Heaviest institutional signal.</li>
            <li><b>Option 02</b> — modern private credit / alternative asset manager. Editorial but current.</li>
            <li><b>Option 03</b> — bulge-bracket investment bank. Widest mainstream appeal.</li>
            <li><b>Option 04</b> — warm, human, family-office feel. Best if you want a "we know you personally" tone.</li>
            <li><b>Option 05</b> — research-house intellectual. Every number matters. Great for sophisticated readers.</li>
            <li><b>Option 06</b> — modern tech-forward finance. Great if borrowers are founders / tech operators.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
